import { colorSystem } from '@luminovo/design-system';
import { Box, Slide } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { useDrawerContext } from '@/shared/data/contexts/ModalContext';
import { useTenantDetailsContext } from '@/shared/data/contexts/TenantDetailsContext';
import { useIsWebSearchEnabled } from '@/shared/permissions/featureFlags';

import { purgeForeignScopes } from './agentChatStorage';
import { LumiAgentPanel } from './LumiAgentPanel';

/**
 * @deprecated The old navigation's Lumi drawer. The new navigation has the Lumi Agent page
 * (`areas/lumi-agent/`); this drawer is deleted with the old navigation in January 2027 (FRONT-603).
 *
 * Hook for managing the Lumi panel.
 *
 * - Handles opening/closing the panel via `useDrawerContext`
 * - Registers global keyboard shortcut CMD+I (Mac) / Ctrl+I (Windows/Linux) to toggle, unless `shortcut` is false:
 *   the new navigation binds it to the Lumi Agent page
 * - Returns `openLumi` function for programmatic opening
 */
export function useLumi({ shortcut }: { shortcut: boolean }): { openLumi: () => void } {
    const { setDrawer, closeDrawer } = useDrawerContext();
    const isWebSearchEnabled = useIsWebSearchEnabled();
    const isOpenRef = useRef(false);
    const { tenantDetails } = useTenantDetailsContext();
    const { user } = useCurrentUserDetailsContext();

    // Purge any Lumi agent chat state on disk that doesn't belong to the
    // current user. This hook is mounted by the global command palette
    // (Navbar) for the whole authenticated session, so this fires on every
    // session boot and whenever the identity changes — covering silent
    // token expiry / SSO account swaps that the explicit logout path
    // wouldn't. Both entry points (Cmd+I hotkey and command bar) route
    // through this same hook instance, so nothing is missed.
    useEffect(() => {
        purgeForeignScopes(tenantDetails.tenant, user.id);
    }, [tenantDetails.tenant, user.id]);

    const openLumi = useCallback(() => {
        isOpenRef.current = true;
        setDrawer(
            <Slide direction="left" in={true} mountOnEnter unmountOnExit>
                <Box
                    sx={{
                        background: 'transparent',
                        width: '100vw',
                        height: '100vh',
                        overflow: 'hidden',
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        zIndex: 9000,
                        // The overlay spans the whole viewport, but only the right
                        // panel holds the chat — the left region is empty. Disable
                        // pointer events here so clicks/scrolls fall through to the
                        // page underneath, then re-enable them on the interactive
                        // parts (resize handle + chat panel) below. This lets users
                        // keep scrolling/working on the page while Lumi stays open.
                        pointerEvents: 'none',
                    }}
                >
                    <PanelGroup direction="horizontal">
                        <Panel defaultSize={70} minSize={50}></Panel>
                        <ResizeHandle />
                        <Panel defaultSize={30} minSize={20} style={{ pointerEvents: 'auto' }}>
                            <LumiAgentPanel
                                onClose={() => {
                                    isOpenRef.current = false;
                                    closeDrawer();
                                }}
                                isWebSearchEnabled={isWebSearchEnabled}
                            />
                        </Panel>
                    </PanelGroup>
                </Box>
            </Slide>,
        );
    }, [setDrawer, closeDrawer, isWebSearchEnabled]);

    const closeLumi = useCallback(() => {
        isOpenRef.current = false;
        closeDrawer();
    }, [closeDrawer]);

    const toggleLumi = useCallback(() => {
        if (isOpenRef.current) {
            closeLumi();
        } else {
            openLumi();
        }
    }, [openLumi, closeLumi]);

    // Keyboard shortcut: CMD+I (Mac) / Ctrl+I (Windows/Linux)
    useHotkeys('mod+i', toggleLumi, {
        enabled: shortcut,
        enableOnFormTags: true,
        preventDefault: true,
    });

    return { openLumi: openLumi };
}

function ResizeHandle() {
    const [isDragging, setIsDragging] = useState(false);
    return (
        <PanelResizeHandle
            onDragging={(isDragging) => setIsDragging(isDragging)}
            style={{
                background: isDragging ? colorSystem.blue[7] : colorSystem.neutral[1],
                width: '2px',
                height: '100%',
                // Re-enable interaction — the parent overlay sets pointerEvents:'none'
                // so the empty left region lets page scroll/clicks through.
                pointerEvents: 'auto',
            }}
        />
    );
}
